import Select from 'react-select';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import Errors from './Errors';

const StyledSelect = styled(Select)`
  color: ${({ theme }) => theme.primaryFontColor};
  font-family: ${({ theme }) => theme.fontFamily};
  font-size: 16px;

  .select__indicator-separator {
    display: none;
  }

  .select__single-value {
    font-weight: 600;
    color: ${({ theme }) => theme.primaryColor};
  }

  svg {
    fill: ${({ theme, errors }) => (errors ? theme.errorColor : theme.primaryColor)};
    transition: all 200ms;
  }

  .select__control {
    border-radius: 5px;
    height: 40px;
    border: 1px solid ${({ theme, errors }) => (errors ? theme.errorColor : theme.disabledBackgroundColor)};
    background-color: ${({ theme }) => theme.cardColor};

    &:hover {
      border: 1px solid ${({ theme, errors }) => (errors ? theme.errorColor : theme.disabledBackgroundColor)};
    }

    .select__placeholder {
      color: ${({ theme }) => theme.disabledFontColor};
    }
  }

  .select__control--is-focused {
    box-shadow: none;
    border: 1px solid ${({ theme, errors }) => (errors ? theme.errorColor : theme.primaryColor)};

    &:hover {
      border: 1px solid ${({ theme, errors }) => (errors ? theme.errorColor : theme.primaryColor)};
    }
  }

  .select__control--menu-is-open {
    border: 1px solid ${({ theme }) => theme.primaryColor};

    svg {
      transform: rotate(180deg);
    }
  }

  .select__control--is-disabled {
    svg {
      fill: ${({ theme }) => theme.disabledFontColor};
    }
    background-color: ${({ theme }) => theme.disabledBackgroundColor};
  }

  .select__single-value--is-disabled {
    color: ${({ theme }) => theme.disabledFontColor};
  }

  .select__menu {
    margin-top: 4px;
    padding: 8px 6px;
    background-color: ${({ theme }) => theme.cardColor};

    .select__menu-list::-webkit-scrollbar {
      width: 4px;
    }

    .select__menu-list::-webkit-scrollbar-track {
      background: ${({ theme }) => theme.secondaryColor};
      border-radius: 2px;
    }

    .select__menu-list::-webkit-scrollbar-thumb {
      background: ${({ theme }) => theme.primaryColor};
      border-radius: 2px;
    }

    .select__option {
      color: ${({ theme }) => theme.primaryFontColor};
      font-weight: 400;
      line-height: 16px;
      margin: 8px 0;
      width: calc(100% - 9px);
      min-height: 26px;
      padding: 5px;
      border-radius: 5px;
      cursor: pointer;
    }

    .select__option--is-selected {
      background-color: inherit;
      color: ${({ theme }) => theme.primaryColor};
      font-weight: 600;
    }

    .select__option--is-focused {
      background-color: ${({ theme }) => theme.secondaryColor};
      color: ${({ theme }) => theme.primaryColor};
    }
  }
`;

const SelectInput = props => {
  const { errors, options, emptyOption, value, disabled } = props;

  const totalOptions = emptyOption && options.length > 0 ? [{ label: '', value: null }, ...options] : options;
  const valueFromOptions = Array.isArray(totalOptions) && totalOptions.find(option => option.value === value);
  return (
    <div>
      <StyledSelect
        {...props}
        options={totalOptions}
        isDisabled={disabled}
        placeholder={disabled ? '' : props.placeholder}
        classNamePrefix={'select'}
        value={(valueFromOptions && !disabled) ? valueFromOptions : null}
        filterOption={(candidate, query) =>
          query ? candidate.label.toUpperCase().startsWith(query.toUpperCase()) : true
        }
      />
      <Errors errors={errors} />
    </div>
  );
};

SelectInput.propTypes = {
  className: PropTypes.string,
  autoFocus: PropTypes.bool,
  errors: PropTypes.oneOfType([PropTypes.array, PropTypes.bool]),
  isSearchable: PropTypes.bool,
  placeholder: PropTypes.string.isRequired,
  name: PropTypes.string,
  noOptionsMessage: PropTypes.func,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  options: PropTypes.arrayOf(PropTypes.shape({ label: PropTypes.string, value: PropTypes.any })).isRequired,
  emptyOptions: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.array, PropTypes.string, PropTypes.number, PropTypes.object, PropTypes.bool]),
};

SelectInput.defaultProps = {
  autoFocus: false,
  className: null,
  errors: null,
  disabled: false,
  isSearchable: true,
  styles: {},
  name: '',
  noOptionsMessage: () => 'Geen resultaten',
  onChange: () => {},
  value: null,
  emptyOption: false,
  maxMenuHeight: 198,
};

export default SelectInput;
